import { AJUDA_FINANCEIRO } from "@/lib/ajuda/dialogos/financeiro";
import { useEffect, useState } from "react";
import { FileUp, Loader2, Paperclip, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@bookdental/ui";
import { hojeEmTexto, type DadosDaNota, type DirecaoDaNota } from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/cn";
import { reais, textoParaCentavos, type NotaDaLista } from "@/lib/dinheiro";
import { campo, rotulo } from "../estilos";
import { LancamentosDaNota } from "./LancamentosDaNota";
import { useArquivoDaNota, type ArquivoEnviado } from "./use-arquivo-da-nota";

const soDigitos = (v: string | null | undefined) => (v ?? "").replace(/\D/g, "");

export function NotaDialog({ aberto, editando, onFechar }: { aberto: boolean; editando: NotaDaLista | null; onFechar: () => void }) {
  const utils = trpc.useUtils();
  const parceiros = trpc.financeiro.lancamentos.parceiros.useQuery(undefined, { enabled: aberto }).data ?? [];
  const { enviar, enviando } = useArquivoDaNota();
  const [direcao, setDirecao] = useState<DirecaoDaNota>("RECEBIDA");
  const [numero, setNumero] = useState("");
  const [serie, setSerie] = useState("");
  const [chave, setChave] = useState("");
  const [emitidaEm, setEmitidaEm] = useState(hojeEmTexto());
  const [valor, setValor] = useState("");
  const [partnerId, setPartnerId] = useState("");
  const [contraparte, setContraparte] = useState("");
  const [lancamentoIds, setLancamentoIds] = useState<string[]>([]);
  const [arquivos, setArquivos] = useState<ArquivoEnviado[]>([]);
  const [lidoDoXml, setLidoDoXml] = useState(false);

  useEffect(() => {
    if (!aberto) return;
    const e = editando;
    setDirecao(e?.direcao ?? "RECEBIDA");
    setNumero(e?.numero ?? "");
    setSerie(e?.serie ?? "");
    setChave(e?.chaveDeAcesso ?? "");
    setEmitidaEm(e?.emitidaEm ?? hojeEmTexto());
    setValor(e ? reais(e.valorCents) : "");
    setPartnerId(e?.partner?.id ?? "");
    setContraparte(e?.contraparte ?? "");
    setLancamentoIds(e?.lancamentos.map((l) => l.id) ?? []);
    setArquivos([]);
    setLidoDoXml(false);
  }, [aberto, editando]);

  function aplicar(d: DadosDaNota) {
    // Laboratório costuma receber NF-e (compra) e emitir NFS-e (serviço); dá para trocar.
    const dir: DirecaoDaNota = d.modelo === "NFE" ? "RECEBIDA" : "EMITIDA";
    const outro = dir === "RECEBIDA" ? d.emitente : d.destinatario;
    setDirecao(dir);
    if (d.numero) setNumero(d.numero);
    if (d.serie) setSerie(d.serie);
    if (d.chaveDeAcesso) setChave(d.chaveDeAcesso);
    if (d.emitidaEm) setEmitidaEm(d.emitidaEm);
    if (d.valorCents) setValor(reais(d.valorCents));
    if (outro.nome) setContraparte(outro.nome);
    const doc = soDigitos(outro.documento);
    const achado = parceiros.find((p) => (doc && soDigitos(p.document) === doc) || (outro.nome && p.name.toLowerCase() === outro.nome.toLowerCase()));
    if (achado) setPartnerId(achado.id);
    setLidoDoXml(true);
  }

  async function anexar(lista: FileList | null) {
    for (const f of Array.from(lista ?? [])) {
      try {
        const { enviado, dados } = await enviar(f);
        setArquivos((a) => [...a, enviado]);
        if (dados) aplicar(dados);
      } catch (e) {
        toast.error((e as Error).message);
      }
    }
  }

  const salvar = trpc.financeiro.notas.salvar.useMutation({
    onSuccess: () => {
      toast.success(editando ? "Nota atualizada." : "Nota registrada.");
      utils.financeiro.invalidate();
      onFechar();
    },
    onError: (e) => toast.error(e.message),
  });
  const valorCents = textoParaCentavos(valor);
  const valido = numero.trim().length > 0 && !!emitidaEm && valorCents > 0 && !enviando;

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && onFechar()}>
      <DialogContent className="max-h-[90dvh] max-w-xl overflow-y-auto border border-zinc-200 bg-white shadow-xl dark:border-border dark:bg-card">
        <DialogHeader>
          <DialogTitle ajuda={AJUDA_FINANCEIRO.nota}>{editando ? "Editar nota fiscal" : "Registrar nota fiscal"}</DialogTitle>
          <DialogDescription>Só o registro: a nota não é emitida por aqui. Anexe o XML para preencher sozinho.</DialogDescription>
        </DialogHeader>

        <label className="flex cursor-pointer flex-col items-center gap-1 rounded-lg border border-dashed border-zinc-300 px-4 py-4 text-center hover:border-brand-400 hover:bg-brand-50/30 dark:border-border dark:hover:bg-primary/5">
          {enviando ? <Loader2 className="h-5 w-5 animate-spin text-brand-600" /> : <FileUp className="h-5 w-5 text-brand-600" />}
          <span className="text-sm font-semibold text-zinc-800 dark:text-foreground">Anexar XML, PDF ou foto da nota</span>
          <span className="text-[11px] text-zinc-500 dark:text-muted-foreground">Até 10 MB por arquivo</span>
          <input type="file" multiple accept=".xml,.pdf,.jpg,.jpeg,.png" className="sr-only" onChange={(e) => anexar(e.target.files)} />
        </label>
        {lidoDoXml && (
          <p className="flex items-center gap-1.5 text-xs text-emerald-700 dark:text-emerald-400">
            <Sparkles className="h-3.5 w-3.5" /> Dados lidos do XML. Confira antes de salvar.
          </p>
        )}
        {(arquivos.length > 0 || (editando?.arquivos.length ?? 0) > 0) && (
          <ul className="space-y-1 text-xs text-zinc-600 dark:text-muted-foreground">
            {[...(editando?.arquivos.map((a) => a.name) ?? []), ...arquivos.map((a) => a.nome)].map((n, i) => (
              <li key={i} className="flex items-center gap-1.5">
                <Paperclip className="h-3.5 w-3.5" /> {n}
              </li>
            ))}
          </ul>
        )}

        <div className="grid grid-cols-2 gap-1 rounded-lg bg-zinc-100 p-1 dark:bg-muted">
          {(["RECEBIDA", "EMITIDA"] as const).map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => {
                setDirecao(d);
                setLancamentoIds([]);
              }}
              className={cn("rounded-md py-1.5 text-sm font-semibold", direcao === d ? "bg-white text-zinc-900 shadow-sm dark:bg-card dark:text-foreground" : "text-zinc-500 dark:text-muted-foreground")}
            >
              {d === "RECEBIDA" ? "Recebida (compra)" : "Emitida (venda)"}
            </button>
          ))}
        </div>

        <div className="grid gap-3 sm:grid-cols-12">
          <label className="sm:col-span-4">
            <span className={rotulo}>Número</span>
            <input value={numero} onChange={(e) => setNumero(e.target.value)} className={campo} />
          </label>
          <label className="sm:col-span-3">
            <span className={rotulo}>Série</span>
            <input value={serie} onChange={(e) => setSerie(e.target.value)} className={campo} />
          </label>
          <label className="sm:col-span-5">
            <span className={rotulo}>Data de emissão</span>
            <input type="date" value={emitidaEm} onChange={(e) => setEmitidaEm(e.target.value)} className={campo} />
          </label>
          <label className="sm:col-span-5">
            <span className={rotulo}>Valor</span>
            <input value={valor} onChange={(e) => setValor(e.target.value)} inputMode="decimal" placeholder="0,00" className={campo} />
          </label>
          <label className="sm:col-span-7">
            <span className={rotulo}>{direcao === "RECEBIDA" ? "Fornecedor" : "Cliente"}</span>
            <select value={partnerId} onChange={(e) => setPartnerId(e.target.value)} className={campo}>
              <option value="">{contraparte ? `Não cadastrado: ${contraparte}` : "Nenhum"}</option>
              {parceiros.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
          <label className="sm:col-span-12">
            <span className={rotulo}>Chave de acesso ou código de verificação</span>
            <input value={chave} onChange={(e) => setChave(e.target.value)} className={cn(campo, "font-mono text-xs")} />
          </label>
        </div>

        <LancamentosDaNota emitidaEm={emitidaEm} tipo={direcao === "EMITIDA" ? "ENTRADA" : "SAIDA"} marcados={lancamentoIds} onChange={setLancamentoIds} />

        <div className="flex justify-end gap-2">
          <button type="button" onClick={onFechar} className="h-9 rounded-md px-4 text-sm text-zinc-600 hover:bg-zinc-100 dark:text-muted-foreground dark:hover:bg-muted">
            Cancelar
          </button>
          <button
            type="button"
            disabled={!valido || salvar.isPending}
            onClick={() =>
              salvar.mutate({
                id: editando?.id,
                direcao,
                numero,
                serie: serie || null,
                chaveDeAcesso: chave || null,
                emitidaEm,
                valorCents,
                partnerId: partnerId || null,
                contraparte: contraparte || null,
                lancamentoIds,
                fileIds: arquivos.map((a) => a.fileId),
              })
            }
            className="inline-flex h-9 items-center gap-2 rounded-md bg-brand-600 px-4 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 disabled:opacity-50"
          >
            {salvar.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Salvar
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
